import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { Input, Select } from '../ui/Input'
import { CustomerOrProspectLookup } from '../ui/CustomerOrProspectLookup'
import { FormValidator } from '../../js/utils/FormUtils'
import { dataService } from '../../services/DataService'
import { useBackendAuth } from '../auth/BackendAuthProvider'

export const QuotationForm = ({ onSubmit, initialData = null }) => {
  const { smmParameters } = useBackendAuth()
  const { t } = useTranslation(['quotation', 'validation', 'common'])

  // Helper function to calculate default dates using SMM parameters
  const calculateDefaultDates = () => {
    const today = new Date()

    // Get days from SMM parameters, fallback to hardcoded values
    const followUpDays = smmParameters?.mserp_alg_followupdate || 180 // Default: 6 months (180 days)
    const expiryDays = smmParameters?.mserp_alg_expirydate || 30 // Default: 30 days

    const followUp = new Date(today)
    followUp.setDate(today.getDate() + followUpDays)

    const expiry = new Date(today)
    expiry.setDate(today.getDate() + expiryDays)

    console.log(`QuotationForm: Using SMM parameters - followUpDays: ${followUpDays}, expiryDays: ${expiryDays}`)

    return {
      followUpDate: followUp.toISOString().split('T')[0],
      expiryDate: expiry.toISOString().split('T')[0]
    }
  }

  const [formData, setFormData] = useState({
    quotationName: initialData?.quotationName || '',
    quotationNumber: initialData?.quotationNumber || '',
    custAccount: initialData?.custAccount || '',
    contactName: initialData?.contactName || '',
    contactEmail: initialData?.contactEmail || '',
    followUpDate: initialData?.followUpDate || calculateDefaultDates().followUpDate,
    expiryDate: initialData?.expiryDate || calculateDefaultDates().expiryDate,
    opportunityId: initialData?.opportunityId || ''
  })

  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(null) // General submission error
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [customerName, setCustomerName] = useState(initialData?.customerName || '')
  const [isNewCustomerOrProspect, setIsNewCustomerOrProspect] = useState(initialData?.isNewCustomerOrProspect || false)
  const [selectedType, setSelectedType] = useState(initialData?.customerType || null) // Track if user selected 'customer' or 'prospect'
  const [isResolvingCustomer, setIsResolvingCustomer] = useState(false) // True while the lookup validates a prefilled value

  // A value is present but not resolved to a concrete customer/prospect (ambiguous or unvalidated prefill)
  const isCustomerUnresolved = !!formData.custAccount && !selectedType
  const isSubmitBlocked = isSubmitting || isResolvingCustomer || isCustomerUnresolved

  // Check if automatic sequence numbering is enabled
  const isAutoSequenceEnabled = smmParameters?.mserp_alg_automaticseqnum === 200000001

  console.log(`QuotationForm: Automatic sequence numbering is ${isAutoSequenceEnabled ? 'enabled' : 'disabled'}. Quotation number field is ${isAutoSequenceEnabled ? 'hidden' : 'visible'}.`)

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      const defaultDates = calculateDefaultDates()
      setFormData({
        quotationName: initialData?.quotationName || '',
        quotationNumber: initialData?.quotationNumber || '',
        custAccount: initialData?.custAccount || '',
        contactName: initialData?.contactName || '',
        contactEmail: initialData?.contactEmail || '',
        followUpDate: initialData?.followUpDate || defaultDates.followUpDate,
        expiryDate: initialData?.expiryDate || defaultDates.expiryDate,
        opportunityId: initialData?.opportunityId || ''
      })
      setCustomerName(initialData?.customerName || '')
      setIsNewCustomerOrProspect(initialData?.isNewCustomerOrProspect || false)
      // The lookup resolves the real type (customer / prospect) once it validates the value
      setSelectedType(initialData.customerType || null)
    }
  }, [initialData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute default dates if SMM parameters arrive later, without touching the other fields
  // (otherwise a customer already resolved by the lookup would be reset to the raw URL text).
  useEffect(() => {
    if (!initialData) return
    const defaultDates = calculateDefaultDates()
    setFormData(prev => ({
      ...prev,
      followUpDate: initialData?.followUpDate || defaultDates.followUpDate,
      expiryDate: initialData?.expiryDate || defaultDates.expiryDate
    }))
  }, [smmParameters]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const resetForm = () => {
    const defaultDates = calculateDefaultDates()
    setFormData({
      quotationName: '',
      quotationNumber: '',
      custAccount: '',
      contactName: '',
      contactEmail: '',
      followUpDate: defaultDates.followUpDate,
      expiryDate: defaultDates.expiryDate,
      opportunityId: ''
    })
    setCustomerName('')
    setIsNewCustomerOrProspect(false)
    setSelectedType(null)
    setErrors({})
    setSubmitError(null)
  }

  // Function to check if customer account exists
  // Now using the selectedType from CustomerOrProspectLookup
  const isExistingCustomer = () => {
    // Return true if user selected a 'customer', false if 'prospect'
    return selectedType === 'customer'
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isResolvingCustomer) return

    const validation = FormValidator.validateQuotationForm({ ...formData, customerType: selectedType })

    // Additional validation for quotation number when automatic sequence is disabled
    if (!isAutoSequenceEnabled && !formData.quotationNumber?.trim()) {
      validation.errors.quotationNumber = 'validation:quotationForm.quotationNumberRequired'
      validation.isValid = false
    }

    if (!validation.isValid) {
      // Resolve i18n keys to translated strings
      const translatedErrors = {}
      Object.entries(validation.errors).forEach(([field, key]) => {
        translatedErrors[field] = t(key)
      })
      setErrors(translatedErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null) // Clear previous error
    try {
      // Check if the user selected a customer or prospect
      const customerExists = isExistingCustomer()

      // Add customer/prospect type info to form data
      const submissionData = {
        ...formData,
        // The SMM configuration is authoritative when automatic numbering is enabled.
        ...(isAutoSequenceEnabled ? { quotationNumber: '' } : {}),
        customerName,
        isNewCustomerOrProspect,
        isExistingCustomer: customerExists
      }

      await onSubmit(submissionData)

      // Clear form fields after successful submission
      const defaultDates = calculateDefaultDates()
      setFormData({
        quotationName: '',
        quotationNumber: '',
        custAccount: '',
        contactName: '',
        contactEmail: '',
        followUpDate: defaultDates.followUpDate,
        expiryDate: defaultDates.expiryDate,
        opportunityId: ''
      })
      setCustomerName('')
      setIsNewCustomerOrProspect(false)

      // Clear selected type and errors
      setSelectedType(null)
      setErrors({})
      setSubmitError(null)
    } catch (error) {
      console.error('Error submitting quotation:', error)

      // Extract user-friendly error message
      let errorMessage = t('quotation:form.errorGeneric')

      if (error.message) {
        // Check if error message contains Dataverse error details
        if (error.message.includes('Number') && error.message.includes('does not match format')) {
          errorMessage = t('quotation:form.errorInvalidNumberFormat')
        } else if (error.message.includes('does not allow editing')) {
          errorMessage = t('quotation:form.errorNumberNotEditable')
        } else {
          errorMessage = error.message
        }
      }

      setSubmitError(errorMessage)
      // Keep form data so user can correct and retry
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card variant="neumorphic" className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
            <span className="text-white text-sm font-bold">Q</span>
          </div>
          <span>{t('quotation:form.title')}</span>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message display */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start space-x-3">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-sm">{submitError}</p>
              </div>
              <button
                type="button"
                onClick={() => setSubmitError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t('quotation:form.quotationName')}
              value={formData.quotationName}
              onChange={(e) => handleInputChange('quotationName', e.target.value)}
              error={errors.quotationName}
              placeholder={t('quotation:form.quotationNamePlaceholder')}
              maxLength={120}
              required
            />

            {/* The SMM configuration controls whether the number is manual or automatic. */}
            {!isAutoSequenceEnabled && (
              <Input
                label={t('quotation:form.quotationNumber')}
                value={formData.quotationNumber}
                onChange={(e) => handleInputChange('quotationNumber', e.target.value)}
                error={errors.quotationNumber}
                placeholder={t('quotation:form.quotationNumberPlaceholder')}
                maxLength={20}
                required
              />
            )}

            <CustomerOrProspectLookup
              label={t('quotation:form.customer')}
              value={formData.custAccount}
              onChange={(value) => handleInputChange('custAccount', value)}
              onTypeChange={(type) => setSelectedType(type)}
              onSelectionChange={(item) => {
                setCustomerName(item?.name || '')
                setIsNewCustomerOrProspect(item?.isNew === true)
              }}
              onResolvingChange={setIsResolvingCustomer}
              error={errors.custAccount}
              placeholder={t('quotation:form.customerPlaceholder')}
              required
            />

            <Input
              label={t('quotation:form.contactNameLabel')}
              value={formData.contactName}
              onChange={(e) => handleInputChange('contactName', e.target.value)}
              error={errors.contactName}
              placeholder={t('quotation:form.contactNamePlaceholder')}
              maxLength={100}
            />

            <Input
              label={t('quotation:form.contactEmailLabel')}
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              error={errors.contactEmail}
              placeholder={t('quotation:form.contactEmailPlaceholder')}
              maxLength={100}
            />

            <Input
              type="date"
              label={t('quotation:form.followUpDateLabel')}
              value={formData.followUpDate}
              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
              error={errors.followUpDate}
              placeholder="dd/mm/yyyy"
              lang="en-GB"
            />

            <Input
              type="date"
              label={t('quotation:form.expiryDateLabel')}
              value={formData.expiryDate}
              onChange={(e) => handleInputChange('expiryDate', e.target.value)}
              error={errors.expiryDate}
              placeholder="dd/mm/yyyy"
              lang="en-GB"
            />

            <Input
              label={t('quotation:form.opportunityLabel')}
              value={formData.opportunityId}
              onChange={(e) => handleInputChange('opportunityId', e.target.value)}
              error={errors.opportunityId}
              placeholder={t('quotation:form.opportunityPlaceholder')}
              maxLength={50}
            />
          </div>

          <motion.div
            className="flex justify-end space-x-4 pt-6 border-t border-neutral-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              type="button"
              variant="secondary"
              onClick={resetForm}
            >
              {t('quotation:form.reset')}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting || isResolvingCustomer}
              disabled={isSubmitBlocked}
              title={isCustomerUnresolved && !isResolvingCustomer ? t('validation:quotationForm.custAccountNotResolved') : undefined}
            >
              {isSubmitting
                ? t('quotation:form.creating')
                : isResolvingCustomer
                  ? t('quotation:form.validatingCustomer')
                  : t('quotation:form.createQuotation')}
            </Button>
          </motion.div>
        </form>
      </CardContent>
    </Card>
  )
}
